// Display vocabulary and formatting for the POC screens. Labels only explain
// codes the backend returned; nothing here decides or changes an outcome.

export const POC_DISCLAIMER =
  "Demonstration warranty configuration only. Decisions are based on synthetic ICMS policies and are not official manufacturer warranty approvals.";

export const CLAIM_STATUSES = ["ACCEPTED", "REJECTED", "MANUAL_REVIEW", "PENDING_EVALUATION", "EVALUATION_ERROR"];

export const STATUS_LABELS = {
  ACCEPTED: "Accepted",
  REJECTED: "Rejected",
  MANUAL_REVIEW: "Manual Review",
  PENDING_EVALUATION: "Pending Evaluation",
  EVALUATION_ERROR: "Evaluation Error",
  DRAFT: "Draft",
  ACTIVE: "Active",
  RETIRED: "Retired",
};

// Value sets of the backend request schemas (policies/models.py, claim_schemas.py).
export const COVERAGE_TYPES = ["BASIC", "POWERTRAIN", "COSMETIC", "EMISSIONS", "HV_BATTERY", "CORROSION"];
export const DISTANCE_UNITS = ["KM", "MI"];
export const LIMIT_RULES = ["WHICHEVER_FIRST", "DURATION_ONLY", "DISTANCE_ONLY"];
export const SOURCE_TYPES = ["ICMS_DEMO_DEFAULT", "CUSTOMER_PROVIDED", "OEM_PUBLISHED"];
export const EXCLUSION_MATCH_TYPES = ["PART_CODE", "COMPONENT_CATEGORY", "SUBLET_CODE", "CONDITION"];
export const SEGMENTS = ["ENTRY", "MID", "PREMIUM"];
// The categories the demonstration policies cover. Not a master list: other upper-case codes are accepted.
export const COMMON_CATEGORIES = ["ENGINE", "TRANSMISSION", "ELECTRICAL", "STEERING"];

export const PATTERNS = {
  vin: /^[A-HJ-NPR-Z0-9]{17}$/,
  category: /^[A-Z][A-Z0-9_]{0,63}$/,
  code: /^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/,
  recordId: /^[A-Z0-9][A-Z0-9_.-]{2,63}$/,
  modelCode: /^[A-Za-z0-9][A-Za-z0-9_-]{0,31}$/,
  market: /^[A-Z0-9][A-Z0-9_-]{0,15}$/,
  reasonCode: /^[A-Z0-9_]{2,16}$/,
  currency: /^[A-Z]{3}$/,
  date: /^\d{4}-\d{2}-\d{2}$/,
};

export const REASON_CODES = {
  WITHIN_PERIOD: "The repair date is within the configured warranty period.",
  OUTSIDE_PERIOD: "The repair date is outside the configured warranty period.",
  PERIOD_BOUNDARY_UNDEFINED: "The warranty period boundary rule is not defined for this case.",
  WITHIN_DISTANCE: "The vehicle is within the configured distance limit.",
  OVER_DISTANCE: "The vehicle has exceeded the configured distance limit.",
  DISTANCE_BOUNDARY_UNDEFINED: "The distance boundary rule is not defined for this case.",
  DISTANCE_UNIT_MISMATCH: "The odometer unit differs from the policy's unit; no conversion is performed.",
  COVERED: "The selected component category is covered by the policy.",
  NOT_COVERED: "The selected component category is not covered by the policy.",
  COVERAGE_NOT_CONFIGURED: "The policy has no coverage list configured.",
  EXCLUSION_MATCHED: "A policy exclusion applies to this claim.",
  EXCLUSION_NOT_MATCHED: "No policy exclusion applies to this claim.",
  UNSUPPORTED_EXCLUSION_TYPE: "The policy contains an exclusion type that cannot be evaluated automatically.",
  MISSING_INPUT: "Information required for this check was not provided.",
  INVALID_DATE_ORDER: "The repair date is before the warranty start date.",
  POLICY_CONFIGURATION_INCOMPLETE: "The selected policy is missing a value this check needs.",
  NO_ACTIVE_POLICY: "No warranty policy is currently active.",
  NO_POLICY_IN_SCOPE: "No active warranty policy applies to this vehicle on the repair date.",
  SCOPE_UNRESOLVED: "The applicable policy could not be determined because required vehicle information (for example model or model year) was not provided.",
  POLICY_CONFLICT: "More than one active policy applies with equal precedence; the conflict needs review.",
  RULES_EVALUATED: "The policy rules were evaluated.",
  NO_ACTIVE_RULE: "No authorization rule is currently active.",
  NO_RULE_IN_SCOPE: "No active authorization rule applies to this vehicle on the repair date.",
  RULE_CONFLICT: "More than one authorization rule applies with equal precedence; the conflict needs review.",
  CURRENCY_MISMATCH: "The claim currency differs from the authorization rule's currency; no conversion is performed.",
  ABOVE_THRESHOLD: "The claim amount is above the authorization threshold, so it needs manual authorization.",
  NOT_ABOVE_THRESHOLD: "The claim amount is within the authorization threshold.",
  UNKNOWN_MODEL: "The model code is not in the vehicle catalogue.",
  UNKNOWN_MANUFACTURER: "The manufacturer code is not in the manufacturer master.",
  MODEL_MANUFACTURER_MISMATCH: "The model belongs to a different manufacturer.",
  EVALUATION_FAILED: "The claim could not be evaluated because of a technical error. It has not been rejected.",
};

export const DECISION_BASIS = {
  ELIGIBLE_WITHIN_THRESHOLD: "Eligible under the warranty policy and within the authorization threshold.",
  WARRANTY_INELIGIBLE: "Not eligible under the applicable warranty policy.",
  NO_APPLICABLE_POLICY: "No applicable warranty policy was found, so the claim needs manual review.",
  WARRANTY_INDETERMINATE: "Warranty eligibility could not be determined from the information provided.",
  AUTHORIZATION_ABOVE_THRESHOLD: "Eligible, but the amount requires manual authorization.",
  NO_APPLICABLE_AUTHORIZATION_RULE: "Eligible, but no authorization rule applies, so the claim needs manual review.",
  AUTHORIZATION_INDETERMINATE: "Eligible, but the authorization check could not be completed.",
  EVALUATION_FAILED: "A technical error stopped the evaluation. This is not a rejection.",
};

export const OUTCOME_LABELS = {
  ELIGIBLE: "Eligible",
  INELIGIBLE: "Not eligible",
  INDETERMINATE: "Could not be determined",
  NO_APPLICABLE_POLICY: "No applicable policy",
  ABOVE_THRESHOLD: "Above threshold",
  NOT_ABOVE_THRESHOLD: "Within threshold",
  NO_APPLICABLE_RULE: "No applicable rule",
};

export const INPUT_LABELS = {
  manufacturer_code: "Manufacturer",
  model_code: "Model",
  model_year: "Model year",
  warranty_start_date: "Warranty start date",
  repair_date: "Repair date",
  distance_reading: "Odometer reading",
  odometer_reading: "Odometer reading",
  distance_unit: "Distance unit",
  coverage_type: "Coverage type",
  component_category: "Component category",
  part_codes: "Part codes",
  sublet_codes: "Sublet codes",
  claimed_amount: "Claim amount",
  claim_amount: "Claim amount",
  currency: "Currency",
};

export const reasonText = (code) => REASON_CODES[code] || null;
export const inputLabel = (name) => INPUT_LABELS[name] || name;
export const statusLabel = (status) => STATUS_LABELS[status] || status || "Unknown";
export const humanize = (code) => (code ? String(code).replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) : "—");

/**
 * Normalizes a user-entered amount to the backend's decimal-string format using
 * string operations only (no floating point): "3,500" -> "3500.00", "12.5" -> "12.50".
 * Returns null when the text is not a non-negative amount with at most two decimals.
 */
export function normalizeAmount(text) {
  const raw = String(text ?? "").replace(/[\s,]/g, "");
  const match = /^(\d{1,13})(?:\.(\d{0,2}))?$/.exec(raw);
  if (!match) return null;
  const whole = match[1].replace(/^0+(?=\d)/, "");
  return `${whole}.${(match[2] || "").padEnd(2, "0")}`;
}

/** "3500.00", "USD" -> "USD 3,500.00" without converting to a number. */
export function formatAmount(amount, currency) {
  if (amount === null || amount === undefined || amount === "") return "—";
  const [whole, fraction] = String(amount).split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${currency ? `${currency} ` : ""}${grouped}${fraction !== undefined ? `.${fraction}` : ""}`;
}

export const formatNumber = (value) =>
  value === null || value === undefined ? "—" : String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/** "2026-10-30T10:15:00Z" -> "2026-10-30 10:15 UTC"; anything else is shown as received. */
export function formatTimestamp(value) {
  if (!value) return "—";
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?Z?$/.exec(value);
  return m ? `${m[1]} ${m[2]} UTC` : String(value);
}

export function isRealDate(value) {
  if (!PATTERNS.date.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/** "A1, B2 ,," -> ["A1", "B2"] */
export const splitCodes = (text) => String(text || "").split(",").map((s) => s.trim()).filter(Boolean);

export function manufacturerName(manufacturers, code) {
  if (!code) return "—";
  const match = (manufacturers || []).find((m) => m.manufacturer_code === code);
  return match ? match.manufacturer_name : code;
}

/** A claim's model: "Hyundai Creta (HYU-CRE)", "HYU-XYZ (not in catalogue)" for an unknown code, else the fallback. */
export function modelLabel(claim, fallback = "—") {
  if (claim?.model_code) {
    const name = String(claim.model_name || "").trim();
    return name ? `${name} (${claim.model_code})` : claim.model_code;
  }
  if (claim?.submitted_model_code) return `${claim.submitted_model_code} (not in catalogue)`;
  return fallback;
}

// The backend refuses a model year later than the repair year + 1 (claim_schemas.py).
export const MODEL_YEAR_AFTER_REPAIR_YEAR = 1;
const MODEL_YEAR_SPAN = 30;     // offered when the catalogue has no release year

/**
 * Model years offered for a model, newest first: from its indicative catalogue
 * release year to the repair year + 1 (the current year + 1 before a repair
 * date is entered). The release year is indicative, so it only bounds the list.
 */
export function modelYearOptions(releaseYear, repairDate, today = new Date()) {
  const repairYear = repairDate && isRealDate(repairDate) ? Number(repairDate.slice(0, 4)) : today.getFullYear();
  const latest = Math.min(2100, repairYear + MODEL_YEAR_AFTER_REPAIR_YEAR);
  const release = Number.isInteger(releaseYear) ? releaseYear : latest - MODEL_YEAR_SPAN;
  const earliest = Math.max(1900, Math.min(release, latest));
  return Array.from({ length: latest - earliest + 1 }, (_, i) => latest - i);
}
